import { useState, useCallback, useRef, useEffect } from "react";
import { usePatronSummary, useCheckout, useCheckin, useRenew } from "@/api/hooks";
import type { Loan, Reservation, PatronStatus, PatronSummaryResponse } from "@/api/types";
import { MessageSquareText, X } from "lucide-react";
import { RenewalLedger } from "../components/RenewalLedger";

type TabType = "checkout" | "checkin" | "renew";

/** Staff comment flagged on the item to be read when it comes back. */
interface CheckinNote {
  itemBarcode: string;
  itemTitle: string | null;
  text: string;
}
/** Tabs driven by scanning an item; renewals are driven by the patron instead. */
type ItemTab = Exclude<TabType, "renew">;

const TABS: { id: TabType; label: string }[] = [
  { id: "checkout", label: "Prêt" },
  { id: "checkin", label: "Retour" },
  { id: "renew", label: "Prolongation" },
];

const ITEM_PANEL_TITLES: Record<ItemTab, string> = {
  checkout: "Exemplaire à prêter",
  checkin: "Exemplaire à retourner",
};

const SUBMIT_LABELS: Record<ItemTab, string> = {
  checkout: "Prêter",
  checkin: "Retourner",
};

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("fr-FR");
}

const STATUS_LABELS: Record<PatronStatus, string> = {
  active: "Actif",
  blocked: "Bloqué",
  expired: "Expiré",
  suspended: "Suspendu",
};

const STATUS_COLORS: Record<PatronStatus, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  blocked: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  expired: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  suspended: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

export function CirculationDeskPage() {
  const [activeTab, setActiveTab] = useState<TabType>("checkout");
  const [patronBarcode, setPatronBarcode] = useState("");
  const [selectedPatronBarcode, setSelectedPatronBarcode] = useState<string | null>(null);
  const [itemBarcode, setItemBarcode] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  // Unlike `message`, it stays until the next return or until dismissed: the
  // agent may need it while handling the item (e.g. checking a missing CD).
  const [checkinNote, setCheckinNote] = useState<CheckinNote | null>(null);

  const patronInputRef = useRef<HTMLInputElement>(null);
  const itemInputRef = useRef<HTMLInputElement>(null);

  const { data: patronSummary, isLoading: patronLoading } = usePatronSummary(
    selectedPatronBarcode ?? undefined
  );

  const checkout = useCheckout();
  const checkin = useCheckin();
  const renew = useRenew();

  // Clear message after 5 seconds
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => {
      setMessage(null);
    }, 5000);
    return () => {
      clearTimeout(timer);
    };
  }, [message]);

  // Focus patron input on load
  useEffect(() => {
    patronInputRef.current?.focus();
  }, []);

  const handlePatronSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (patronBarcode.trim()) {
        setSelectedPatronBarcode(patronBarcode.trim());
        setMessage(null);
        // Focus item input after selecting patron
        if (activeTab === "checkout") setTimeout(() => itemInputRef.current?.focus(), 100);
      }
    },
    [patronBarcode, activeTab]
  );

  const handleClearPatron = useCallback(() => {
    setSelectedPatronBarcode(null);
    setPatronBarcode("");
    setMessage(null);
    patronInputRef.current?.focus();
  }, []);

  const handleCheckout = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedPatronBarcode || !itemBarcode.trim()) return;

      try {
        const result = await checkout.mutateAsync({
          patronBarcode: selectedPatronBarcode,
          itemBarcode: itemBarcode.trim(),
        });
        setMessage({
          type: "success",
          text: `Prêt effectué - Retour le ${new Date(result.loan.dueDate).toLocaleDateString("fr-FR")}`,
        });
        setItemBarcode("");
        itemInputRef.current?.focus();
      } catch (err) {
        setMessage({
          type: "error",
          text: err instanceof Error ? err.message : "Erreur lors du prêt",
        });
      }
    },
    [selectedPatronBarcode, itemBarcode, checkout]
  );

  const handleCheckin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!itemBarcode.trim()) return;
      setCheckinNote(null);

      try {
        const result = await checkin.mutateAsync({
          itemBarcode: itemBarcode.trim(),
        });
        if (result.checkinNote) {
          setCheckinNote({
            itemBarcode: result.loan?.itemBarcode ?? itemBarcode.trim(),
            itemTitle: result.loan?.itemTitle ?? null,
            text: result.checkinNote,
          });
        }
        let text = "Retour effectué";
        if (result.wasOverdue && result.fineAmount > 0) {
          text += ` - Amende: ${(result.fineAmount / 100).toFixed(2)} €`;
        }
        if (result.nextReservation) {
          text += ` - Réservé par ${result.nextReservation.patronName}`;
          if (result.needsTransfer && result.transferTo) {
            text += ` (transfert vers ${result.transferTo})`;
          }
        }
        setMessage({ type: "success", text });
        setItemBarcode("");
        itemInputRef.current?.focus();
      } catch (err) {
        setMessage({
          type: "error",
          text: err instanceof Error ? err.message : "Erreur lors du retour",
        });
      }
    },
    [itemBarcode, checkin]
  );

  const handleRenew = useCallback(
    async (loan: Loan) => {
      try {
        const result = await renew.mutateAsync({ loanId: loan.id });
        setMessage({
          type: "success",
          text: `Prolongation effectuée - Nouveau retour le ${formatDate(result.newDueDate)}`,
        });
      } catch (err) {
        setMessage({
          type: "error",
          text: err instanceof Error ? err.message : "Erreur lors de la prolongation",
        });
      }
    },
    [renew]
  );

  const handleTabChange = useCallback(
    (tab: TabType) => {
      setActiveTab(tab);
      setMessage(null);
      setCheckinNote(null);
      // Wait for the target input to be rendered/enabled before focusing it
      setTimeout(() => {
        if (tab === "renew" || (tab === "checkout" && !selectedPatronBarcode)) {
          patronInputRef.current?.focus();
        } else {
          itemInputRef.current?.focus();
        }
      }, 0);
    },
    [selectedPatronBarcode]
  );

  const itemSubmitHandlers: Record<ItemTab, (e: React.FormEvent) => Promise<void>> = {
    checkout: handleCheckout,
    checkin: handleCheckin,
  };
  const isItemActionPending = checkout.isPending || checkin.isPending;
  const isPatronTab = activeTab === "checkout" || activeTab === "renew";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Circulation</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={activeTab === tab.id}
              onClick={() => {
                handleTabChange(tab.id);
              }}
              className={`border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          role={message.type === "error" ? "alert" : "status"}
          className={`rounded-lg p-4 text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      <div
        className={`grid gap-6 ${
          activeTab === "renew" ? "lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]" : "lg:grid-cols-2"
        }`}
      >
        {/* Left panel: Patron / Item scan */}
        <div className="space-y-6">
          {/* Patron search (checkout and renewal modes) */}
          {isPatronTab && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Lecteur</h2>
              {!selectedPatronBarcode ? (
                <form onSubmit={handlePatronSearch}>
                  <div className="flex gap-3">
                    <input
                      ref={patronInputRef}
                      type="text"
                      value={patronBarcode}
                      onChange={(e) => {
                        setPatronBarcode(e.target.value);
                      }}
                      placeholder="Scanner la carte lecteur..."
                      aria-label="Carte lecteur"
                      className="min-w-0 flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="shrink-0 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 sm:px-6"
                    >
                      Rechercher
                    </button>
                  </div>
                </form>
              ) : patronLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                </div>
              ) : patronSummary ? (
                <PatronCard
                  summary={patronSummary}
                  onClear={handleClearPatron}
                  showBorrowWarning={activeTab === "checkout"}
                />
              ) : (
                <div className="py-4 text-center text-gray-500 dark:text-gray-400">
                  Lecteur non trouvé
                  <button
                    onClick={handleClearPatron}
                    className="ml-4 text-blue-600 hover:underline"
                  >
                    Effacer
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Item scan */}
          {activeTab !== "renew" && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                {ITEM_PANEL_TITLES[activeTab]}
              </h2>
              <form onSubmit={itemSubmitHandlers[activeTab]}>
                <div className="flex gap-3">
                  <input
                    ref={itemInputRef}
                    type="text"
                    value={itemBarcode}
                    onChange={(e) => {
                      setItemBarcode(e.target.value);
                    }}
                    placeholder="Scanner le code-barres de l'exemplaire..."
                    aria-label={ITEM_PANEL_TITLES[activeTab]}
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    disabled={activeTab === "checkout" && !selectedPatronBarcode}
                  />
                  <button
                    type="submit"
                    disabled={
                      (activeTab === "checkout" && !selectedPatronBarcode) ||
                      !itemBarcode.trim() ||
                      isItemActionPending
                    }
                    className="shrink-0 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:px-6"
                  >
                    {isItemActionPending ? "..." : SUBMIT_LABELS[activeTab]}
                  </button>
                </div>
              </form>
              {activeTab === "checkin" && checkinNote && (
                <CheckinNotePanel
                  note={checkinNote}
                  onDismiss={() => {
                    setCheckinNote(null);
                    itemInputRef.current?.focus();
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Right panel: Quick info */}
        <div className="space-y-6">
          {activeTab === "renew" &&
            (selectedPatronBarcode && patronSummary ? (
              // Keyed by patron so selection and outcomes never leak to the next patron
              <RenewalLedger key={selectedPatronBarcode} patronBarcode={selectedPatronBarcode} />
            ) : (
              !selectedPatronBarcode && (
                <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Prolonger les prêts d&apos;un lecteur
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Scannez sa carte : ses prêts en cours s&apos;affichent ici, avec ce que la
                    prolongation changera pour chacun.
                  </p>
                </div>
              )
            ))}

          {activeTab === "checkout" && patronSummary && (
            <>
              {/* Current loans */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                  Prêts en cours ({patronSummary.loans.length})
                </h2>
                {patronSummary.loans.length > 0 ? (
                  <div className="space-y-3">
                    {patronSummary.loans.map((loan) => (
                      <LoanRow key={loan.id} loan={loan} onRenew={handleRenew} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucun prêt en cours</p>
                )}
              </div>

              {/* Reservations */}
              {patronSummary.reservations.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                  <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                    Réservations ({patronSummary.reservations.length})
                  </h2>
                  <div className="space-y-3">
                    {patronSummary.reservations.map((res) => (
                      <ReservationRow key={res.id} reservation={res} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PatronCard({
  summary,
  onClear,
  showBorrowWarning,
}: {
  summary: PatronSummaryResponse;
  onClear: () => void;
  /** The renewal ledger explains account restrictions itself */
  showBorrowWarning: boolean;
}) {
  const { patron, limits, overdueCount } = summary;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{patron.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {patron.barcode} - {patron.category}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[patron.status]}`}
          >
            {STATUS_LABELS[patron.status]}
          </span>
          <button
            onClick={onClear}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            title="Changer de lecteur"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Warnings */}
      {showBorrowWarning && !patron.canBorrow && (
        <div className="rounded-lg bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <strong>Impossible de prêter</strong>
          {patron.statusReason && `: ${patron.statusReason}`}
        </div>
      )}
      {overdueCount > 0 && (
        <div className="rounded-lg bg-yellow-100 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          {overdueCount} document{overdueCount > 1 ? "s" : ""} en retard
        </div>
      )}
      {patron.fineBalance > 0 && (
        <div className="rounded-lg bg-orange-100 p-3 text-sm text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
          Amende: {(patron.fineBalance / 100).toFixed(2)} €
        </div>
      )}

      {/* Limits */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Prêts:</span>{" "}
          <span
            className={limits.currentLoans >= limits.maxLoans ? "font-medium text-red-600" : ""}
          >
            {limits.currentLoans} / {limits.maxLoans}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Réservations:</span>{" "}
          <span
            className={
              limits.currentReservations >= limits.maxReservations ? "font-medium text-red-600" : ""
            }
          >
            {limits.currentReservations} / {limits.maxReservations}
          </span>
        </div>
      </div>

      {/* Expiration */}
      {patron.expirationDate && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Expire le {new Date(patron.expirationDate).toLocaleDateString("fr-FR")}
        </p>
      )}
    </div>
  );
}

function LoanRow({ loan, onRenew }: { loan: Loan; onRenew: (loan: Loan) => void }) {
  const dueDate = new Date(loan.dueDate);
  const isOverdue = loan.isOverdue;

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/50">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
          {loan.itemTitle}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{loan.itemBarcode}</p>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={`text-xs ${isOverdue ? "font-medium text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}
        >
          {isOverdue
            ? `${String(loan.daysOverdue)}j de retard`
            : `Retour ${dueDate.toLocaleDateString("fr-FR")}`}
        </span>
        {loan.canRenew && (
          <button
            onClick={() => {
              onRenew(loan);
            }}
            className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            Prolonger
          </button>
        )}
      </div>
    </div>
  );
}

function ReservationRow({ reservation }: { reservation: Reservation }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/50">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
          {reservation.biblioRecordTitle}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Position: {reservation.queuePosition} / {reservation.totalInQueue}
        </p>
      </div>
      <div>
        <span
          className={`rounded-full px-2 py-1 text-xs font-medium ${
            reservation.status === "ready"
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
          }`}
        >
          {reservation.status === "ready"
            ? "Disponible"
            : reservation.status === "to_prepare"
              ? "À préparer"
              : "En attente"}
        </span>
      </div>
    </div>
  );
}

export default CirculationDeskPage;

function CheckinNotePanel({ note, onDismiss }: { note: CheckinNote; onDismiss: () => void }) {
  return (
    <section
      role="alert"
      aria-label="Commentaire sur l'exemplaire"
      className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-950/40"
    >
      <div className="flex items-start gap-3">
        <MessageSquareText
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Commentaire sur l'exemplaire
          </h3>
          <p className="mt-0.5 truncate text-xs text-amber-800 dark:text-amber-300">
            <span className="font-mono">{note.itemBarcode}</span>
            {note.itemTitle && ` · ${note.itemTitle}`}
          </p>
          <p className="mt-2 text-sm whitespace-pre-line text-amber-950 dark:text-amber-50">
            {note.text}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fermer le commentaire"
          className="shrink-0 rounded-md p-1 text-amber-800 hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none dark:text-amber-300 dark:hover:bg-amber-900/40"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </section>
  );
}
